// src/components/DealShareModal.js
// "Manage access" modal for private deals: lists the deal's members (DEAL-scoped role
// holders), adds members via the grantable-users picker, and removes members by
// resubmitting the user's roles without the deal role (mirrors user management).

import React, { useState, useEffect } from "react";
import { X, Loader, Trash2, Lock, UserPlus } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { fetchUserRoles, updateUserRoles } from "../services/userRolesService";
import Button from "./Buttons";

// Role/scope values mirror the backend enums (see userRolesService.ORG_INVITE_ROLES comment)
// Role values: OWNER=1, ADMIN=2, RISK_MANAGER=3, CONTRIBUTOR=4, APPROVER=5, VIEWER=6
// Scope values: ACCOUNT=1, TENANT=2, ORG=3, BUSINESS_UNIT=4, DEAL=5, SELF=6
const DEAL_SCOPE = 5;
const DEFAULT_ROLE = 4; // CONTRIBUTOR

// Ordered least -> most access. At DEAL scope the role decides BREADTH, not just strength
// (backend RoleSemantics): Contributor and Approver see only what is assigned to them, so they
// sit BELOW Viewer in what they can see even though their role value is stronger.
const DEAL_ROLE_OPTIONS = [
    { value: 5, label: "Approver", description: "Approves only the risks they are named on. Sees nothing else in the deal." },
    { value: 4, label: "Contributor", description: "Works only on assessments and risks assigned to them. Sees nothing else in the deal." },
    { value: 6, label: "Viewer", description: "Reads everything in the deal. Changes nothing." },
    { value: 3, label: "Risk Manager", description: "Full access to the deal — assessments, risks, and remediations." },
    { value: 2, label: "Admin", description: "Full access, and can manage who else has access." },
    { value: 1, label: "Owner", description: "Full access, manages access, and controls whether the deal is private." },
];

const ROLE_LABELS = {
    OWNER: "Owner",
    ADMIN: "Admin",
    RISK_MANAGER: "Risk Manager",
    CONTRIBUTOR: "Contributor",
    APPROVER: "Approver",
    VIEWER: "Viewer",
};

const DealShareModal = ({ isOpen, onClose, dealId, dealName }) => {
    const apiCall = useApi();
    const { user } = useAuth();

    const [members, setMembers] = useState([]);
    const [isPrivate, setIsPrivate] = useState(false);
    const [grantableUsers, setGrantableUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [selectedRole, setSelectedRole] = useState(DEFAULT_ROLE);
    const selectedRoleDescription =
        DEAL_ROLE_OPTIONS.find((opt) => opt.value === selectedRole)?.description;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [removingUserId, setRemovingUserId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (isOpen && dealId) {
            setSelectedUserId("");
            setSelectedRole(DEFAULT_ROLE);
            setError("");
            loadData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, dealId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [membersRes, grantableRes] = await Promise.all([
                apiCall("GET", `/deals/${dealId}/members`),
                apiCall("GET", `/deals/${dealId}/grantable-users`),
            ]);
            setMembers(membersRes?.members || []);
            setIsPrivate(!!membersRes?.isPrivate);
            setGrantableUsers(grantableRes || []);
        } catch (err) {
            setError("Failed to load deal access data.");
        } finally {
            setLoading(false);
        }
    };

    // Rebuild the target user's role list with the deal role replaced (or removed when
    // dealRoleValue is null) and resubmit via POST /users/{id}/roles — the same
    // replace-all-manageable-roles flow user management uses. Roles outside the caller's
    // manageable scope are preserved server-side.
    const submitRoles = async (userId, dealRoleValue) => {
        const existing = (await fetchUserRoles(apiCall, userId)) || [];
        const kept = existing
            .filter(r => !(r.scopeValue === DEAL_SCOPE && r.scopeEntityId === dealId))
            .map(r => ({ role: r.roleValue, scope: r.scopeValue, scopeEntityId: r.scopeEntityId }));
        const payload = dealRoleValue == null
            ? kept
            : [...kept, { role: dealRoleValue, scope: DEAL_SCOPE, scopeEntityId: dealId }];
        await updateUserRoles(apiCall, userId, payload);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!selectedUserId) {
            setError("Please select a user.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await submitRoles(parseInt(selectedUserId, 10), parseInt(selectedRole, 10));
            setSelectedUserId("");
            setSelectedRole(DEFAULT_ROLE);
            await loadData();
        } catch (err) {
            setError("Failed to share the deal. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (member) => {
        setRemovingUserId(member.userId);
        setError("");
        try {
            await submitRoles(member.userId, null);
            await loadData();
        } catch (err) {
            setError("Failed to remove access. Please try again.");
        } finally {
            setRemovingUserId(null);
        }
    };

    if (!isOpen) return null;

    // Users not already members are offered in the picker
    const memberIds = new Set(members.map(m => m.userId));
    const availableUsers = grantableUsers.filter(u => !memberIds.has(u.id));

    return (
        <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50">
            <div className="bg-theme-secondary p-6 rounded-lg w-[640px] max-w-[95vw] shadow-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="text-xl font-semibold text-content-primary">Manage access</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>
                {dealName && (
                    <p className="text-sm text-content-secondary mb-1">{dealName}</p>
                )}
                {isPrivate && (
                    <p className="flex items-center gap-1.5 text-xs text-content-muted mb-4">
                        <Lock className="h-3.5 w-3.5 shrink-0" />
                        This deal is private — only the people listed below can see it, organization admins included.
                    </p>
                )}

                {error && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        {/* Members */}
                        <h3 className="text-sm font-medium text-content-secondary mb-2 mt-3">Members</h3>
                        {members.length === 0 ? (
                            <p className="text-sm text-gray-500 italic mb-4">No members yet.</p>
                        ) : (
                            <ul className="space-y-2 mb-4">
                                {members.map((member) => (
                                    <li
                                        key={`${member.userId}-${member.role}`}
                                        className="flex items-center justify-between p-2 border border-border rounded-md"
                                    >
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-content-primary truncate">
                                                {member.fullName || member.email || `User ${member.userId}`}
                                                {member.userId === user?.id && (
                                                    <span className="ml-1 text-xs text-content-muted">(you)</span>
                                                )}
                                            </p>
                                            {member.email && (
                                                <p className="text-xs text-gray-500 truncate">{member.email}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-2">
                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                                {ROLE_LABELS[member.role] || member.role}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemove(member)}
                                                disabled={member.userId === user?.id || removingUserId !== null}
                                                title={member.userId === user?.id
                                                    ? "You cannot remove your own access"
                                                    : "Remove access"}
                                                className="text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {removingUserId === member.userId
                                                    ? <Loader className="h-4 w-4 animate-spin" />
                                                    : <Trash2 className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Add member */}
                        <h3 className="text-sm font-medium text-content-secondary mb-2">Share with</h3>
                        <form onSubmit={handleAdd} className="flex items-center gap-2">
                            <select
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                className="flex-1 min-w-0 border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                            >
                                <option value="">-- Select User --</option>
                                {availableUsers.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.fullName && u.email
                                            ? `${u.fullName} (${u.email})`
                                            : (u.fullName || u.email)}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(parseInt(e.target.value, 10))}
                                className="border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                            >
                                {DEAL_ROLE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <Button type="submit" size="sm" disabled={saving || !selectedUserId}>
                                {saving
                                    ? <Loader className="h-4 w-4 animate-spin" />
                                    : (<><UserPlus className="h-4 w-4 mr-1" />Add</>)}
                            </Button>
                        </form>

                        {selectedRoleDescription && (
                            <p className="text-xs text-content-muted mt-2">{selectedRoleDescription}</p>
                        )}

                        <div className="flex justify-end mt-6">
                            <Button type="button" variant="secondary" onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DealShareModal;
